import {
  forwardRef,
  type MouseEventHandler,
  type ReactNode,
  type Ref,
} from 'react';
import clsx from 'clsx';
import {
  Loading,
  LoadingDotColorVariant,
} from '@hungryroot/loading-component-primitives';
import { Heading } from '../typography/Heading/Heading';
import { Paragraph } from '../typography/Paragraph/Paragraph';
import styles from './Button.module.css';

export type StyleVariant =
  | 'muted'
  | 'inverted'
  | 'default'
  | 'success'
  | 'warning'
  | 'critical'
  | 'disabled'
  | 'accent';

type AccentButtonStyle =
  | 'grapefruit'
  | 'peanut butter'
  | 'avocado'
  | 'elderberry'
  | 'fig';

// Tertiary and Link are similar, but Link text is underlined and Tertiary is not
export type ProminenceVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'tertiary-underlined';

export type SizeVariant =
  | 'small'
  | 'medium'
  | 'large'
  | 'small-with-icon'
  | 'medium-with-icon'
  | 'large-with-icon';

export type IconPositionVariant = 'left' | 'right';

export type WidthVariant = 'full-width' | 'full-width-mobile';

interface BaseIconProps {
  icon?: ReactNode;
  iconPosition?: IconPositionVariant;
}

interface NoIconProps extends BaseIconProps {
  icon?: undefined;
  iconPosition?: undefined;
}

interface IconProps extends BaseIconProps {
  icon: ReactNode;
  iconPosition: IconPositionVariant;
}

type ButtonIconProps = IconProps | NoIconProps;

interface BaseButtonProps {
  testid?: string;
  style: StyleVariant;
  prominence: ProminenceVariant;
  size: SizeVariant;
  className?: string;
  children?: ReactNode;
  width?: WidthVariant;
  textPrimary?: ReactNode;
  textSecondary?: ReactNode;
  centerContent?: boolean;
  iconSecondary?: ReactNode;
  loading?: boolean;
  accentColor?: AccentButtonStyle;
  buttonType?: 'link' | 'submit' | 'button' | 'visual';
  disabled?: boolean;
  onClick?:
    | MouseEventHandler<HTMLAnchorElement>
    | MouseEventHandler<HTMLButtonElement>;
}

interface LinkButtonProps extends BaseButtonProps {
  buttonType: 'link';
  to: string;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  openLinkInNewTab?: boolean;
}

export interface SubmitButtonProps extends BaseButtonProps {
  buttonType?: 'submit' | 'button';
  onClick?: MouseEventHandler<HTMLButtonElement>;
  form?: string;
}

interface VisualButtonProps extends BaseButtonProps {
  buttonType: 'visual';
}

export type Props = (LinkButtonProps | SubmitButtonProps | VisualButtonProps) &
  ButtonIconProps;

function getVariantClass(
  style: StyleVariant,
  prominence: ProminenceVariant,
  size: SizeVariant,
  accentStyle: AccentButtonStyle,
  width?: WidthVariant,
  centerContent?: boolean,
  loading?: boolean
) {
  const className =
    style !== 'accent'
      ? styles[`${style}-${prominence}`]
      : clsx({
          // Grapefruit
          [styles['grapefruit-tertiary']]:
            accentStyle === 'grapefruit' && prominence === 'tertiary',
          [styles['grapefruit-secondary']]:
            accentStyle === 'grapefruit' && prominence === 'secondary',
          [styles.grapefruit]:
            accentStyle === 'grapefruit' && prominence === 'primary',

          // Peanut butter
          [styles['peanut-butter-tertiary']]:
            accentStyle === 'peanut butter' && prominence === 'tertiary',
          [styles['peanut-butter-secondary']]:
            accentStyle === 'peanut butter' && prominence === 'secondary',
          [styles['peanut-butter']]:
            accentStyle === 'peanut butter' && prominence === 'primary',

          // Avocado
          [styles['avocado-tertiary']]:
            accentStyle === 'avocado' && prominence === 'tertiary',
          [styles['avocado-secondary']]:
            accentStyle === 'avocado' && prominence === 'secondary',
          [styles.avocado]:
            accentStyle === 'avocado' && prominence === 'primary',

          // elderberry
          [styles['elderberry-tertiary']]:
            accentStyle === 'elderberry' && prominence === 'tertiary',
          [styles['elderberry-secondary']]:
            accentStyle === 'elderberry' && prominence === 'secondary',
          [styles.elderberry]:
            accentStyle === 'elderberry' && prominence === 'primary',

          // fig
          [styles['fig-tertiary']]:
            accentStyle === 'fig' && prominence === 'tertiary',
          [styles['fig-secondary']]:
            accentStyle === 'fig' && prominence === 'secondary',
          [styles.fig]: accentStyle === 'fig' && prominence === 'primary',
        });

  return [
    className,
    styles[`${size}`],
    width ? styles[`${width}`] : '',
    centerContent || centerContent === undefined
      ? styles['center']
      : styles['space-between'],
    loading ? styles['loading'] : '',
  ].join(' ');
}

const getButtonTextVariant = (size?: SizeVariant) => {
  switch (size) {
    case 'small':
    case 'small-with-icon':
      return 'h7';
    case 'medium':
    case 'medium-with-icon':
      return 'H6';
    case 'large':
    case 'large-with-icon':
      return 'H5';
  }
  return 'h7';
};

function getButtonContent(
  textPrimary: string | ReactNode,
  textSecondary?: string | ReactNode,
  icon?: ReactNode,
  iconPosition?: IconPositionVariant,
  iconSecondary?: ReactNode,
  testid?: string,
  size?: SizeVariant
) {
  const buttonTextVariant = getButtonTextVariant(size);

  const getIcon = () => {
    const iconVariant = icon || iconSecondary;
    return iconVariant;
  };

  if (iconPosition === 'left' && icon) {
    return (
      <>
        <span
          data-testid={`${testid}-icon-left`}
          className={styles['icon-left']}
        >
          {getIcon()}
        </span>
        {buttonTextVariant === 'h7' ? (
          <Paragraph variant={buttonTextVariant}>{textPrimary}</Paragraph>
        ) : (
          <Heading variant={buttonTextVariant}>{textPrimary}</Heading>
        )}
      </>
    );
  }

  if (iconPosition === 'right' && icon) {
    return (
      <>
        {buttonTextVariant === 'h7' ? (
          <Paragraph variant={buttonTextVariant}>{textPrimary}</Paragraph>
        ) : (
          <Heading variant={buttonTextVariant}>{textPrimary}</Heading>
        )}
        <span
          data-testid={`${testid}-icon-right`}
          className={styles['icon-right']}
        >
          {getIcon()}
        </span>
      </>
    );
  }

  if (textSecondary) {
    return (
      <>
        {buttonTextVariant === 'h7' ? (
          <Paragraph variant={buttonTextVariant}>{textPrimary}</Paragraph>
        ) : (
          <Heading variant={buttonTextVariant}>{textPrimary}</Heading>
        )}

        {buttonTextVariant === 'h7' ? (
          <Paragraph
            variant={buttonTextVariant}
            testid={`${testid}-secondary-text-wrapper`}
            className={styles['secondary-text-wrapper']}
          >
            {iconSecondary ? getIcon() : null}
            {textSecondary}
          </Paragraph>
        ) : (
          <Heading
            variant={buttonTextVariant}
            testid={`${testid}-secondary-text-wrapper`}
            className={styles['secondary-text-wrapper']}
          >
            {iconSecondary ? getIcon() : null}
            {textSecondary}
          </Heading>
        )}
      </>
    );
  }

  return buttonTextVariant === 'h7' ? (
    <Paragraph variant={buttonTextVariant}>{textPrimary}</Paragraph>
  ) : (
    <Heading variant={buttonTextVariant}>{textPrimary}</Heading>
  );
}

function getLoadingContent(
  loading?: boolean,
  testid?: string,
  prominence?: ProminenceVariant,
  style?: StyleVariant,
  size?: SizeVariant
) {
  if (loading) {
    const isNotLarge = size !== 'large' && size !== 'large-with-icon';

    if (prominence === 'primary') {
      if (style === 'inverted') {
        return (
          <Loading
            testid={`${testid}-loading`}
            variant="inline-button"
            dotColor={LoadingDotColorVariant.ColorNeutral500}
            sizeVariant={isNotLarge ? 'small' : undefined}
          />
        );
      }
      return (
        <Loading
          testid={`${testid}-loading`}
          variant="inline-button"
          dotColor={LoadingDotColorVariant.ColorNeutral0}
          sizeVariant={isNotLarge ? 'small' : undefined}
        />
      );
    } else {
      return (
        <Loading
          variant="inline-button"
          sizeVariant={isNotLarge ? 'small' : undefined}
        />
      );
    }
  }
}

const getVisualVariantTypeButtonContent = ({
  loading,
  testid,
  prominence,
  style,
  size,
  children,
  textPrimary,
  textSecondary,
  icon,
  iconPosition,
  iconSecondary,
}: {
  loading?: boolean;
  testid?: string;
  prominence?: ProminenceVariant;
  style?: StyleVariant;
  size?: SizeVariant;
  children?: ReactNode;
  textPrimary?: string | ReactNode;
  textSecondary?: string | ReactNode;
  icon?: ReactNode;
  iconPosition?: IconPositionVariant;
  iconSecondary?: ReactNode;
}) => {
  if (loading) {
    return getLoadingContent(loading, testid, prominence, style, size);
  }

  if (children) {
    return children;
  }

  return getButtonContent(
    textPrimary,
    textSecondary,
    icon,
    iconPosition,
    iconSecondary,
    testid,
    size
  );
};

export const Button = forwardRef(
  (props: Props, ref: Ref<HTMLButtonElement | HTMLAnchorElement>) => {
    const {
      testid,
      style,
      prominence,
      size,
      iconPosition,
      className,
      icon,
      width,
      textPrimary,
      textSecondary,
      centerContent,
      iconSecondary,
      loading,
      children,
      accentColor: accentStyle = 'grapefruit',
    } = props;

    const buttonIsDisabled = style === 'disabled' || props.disabled;
    const effectiveStyle = props.disabled ? 'disabled' : style;

    if (props.buttonType === 'link') {
      const { to, openLinkInNewTab, onClick } = props;
      return (
        <a
          {...(buttonIsDisabled ? {} : { href: to })}
          target={openLinkInNewTab ? '_blank' : '_self'}
          ref={ref as Ref<HTMLAnchorElement>}
          data-testid={testid}
          className={`${getVariantClass(
            effectiveStyle,
            prominence,
            size,
            accentStyle,
            width,
            centerContent
          )} ${className}`}
          onClick={buttonIsDisabled ? undefined : onClick}
        >
          {getButtonContent(
            textPrimary,
            textSecondary,
            icon,
            iconPosition,
            iconSecondary,
            testid,
            size
          )}
        </a>
      );
    }

    if (props.buttonType === 'visual') {
      return (
        <span
          data-testid={testid}
          className={`${getVariantClass(
            effectiveStyle,
            prominence,
            size,
            accentStyle,
            width,
            centerContent
          )} ${className}`}
        >
          {getVisualVariantTypeButtonContent({
            loading,
            testid,
            prominence,
            style: effectiveStyle,
            size,
            children,
            textPrimary,
            textSecondary,
            icon,
            iconPosition,
            iconSecondary,
          })}
        </span>
      );
    }

    return (
      <button
        ref={ref as Ref<HTMLButtonElement>}
        data-testid={testid}
        className={`${getVariantClass(
          effectiveStyle,
          prominence,
          size,
          accentStyle,
          width,
          centerContent,
          loading
        )} ${className}`}
        onClick={buttonIsDisabled ? undefined : props.onClick}
        type={props.buttonType}
        disabled={buttonIsDisabled || loading}
        form={props.buttonType === 'submit' ? props.form : undefined}
      >
        {getLoadingContent(loading, testid, prominence, effectiveStyle, size)}

        {children
          ? children
          : getButtonContent(
              textPrimary,
              textSecondary,
              icon,
              iconPosition,
              iconSecondary,
              testid,
              size
            )}
      </button>
    );
  }
);
